import React from 'react'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'
import Button from '../../ui/Button'
import type { AppendixCodeItemOption } from '../../../types/codeIndex'
import { getCellErrorKey } from '../../../hooks/useAppendixTableDraft'
import {
  AppendixColumnDraft,
  AppendixRowDraft,
  findCodeItemOption,
  formatColumnHeader,
} from '../../../utils/appendixTable'
import AppendixCellInput from './AppendixCellInput'
import { CodeItemChip } from './AppendixColumnsSection'

type AppendixRowsSectionProps = {
  columns: AppendixColumnDraft[]
  rows: AppendixRowDraft[]
  codeItems: AppendixCodeItemOption[]
  cellErrors: Record<string, string>
  firstCellError: string | null
  disabled?: boolean
  readOnly?: boolean
  onAddRow: () => void
  onDeleteRow: (rowKey: string) => void
  onUpdateCell: (rowKey: string, columnKey: string, value: string, validateNow?: boolean) => void
  onValidateCell: (rowKey: string, columnKey: string) => void
}

const EmptyRowsState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-[88px] items-center justify-center rounded-lg border border-dashed border-[#CBD5E1] px-4 text-sm text-gray-500">
    {children}
  </div>
)

export const AppendixRowsSection: React.FC<AppendixRowsSectionProps> = ({
  columns,
  rows,
  codeItems,
  cellErrors,
  firstCellError,
  disabled = false,
  readOnly = false,
  onAddRow,
  onDeleteRow,
  onUpdateCell,
  onValidateCell,
}) => {
  const gridTemplateColumns = `repeat(${columns.length}, minmax(120px, 1fr))${readOnly ? '' : ' 32px'}`

  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Rows</h3>
        {!readOnly ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="bg-[#EEF6FF]"
            onClick={onAddRow}
            disabled={disabled || columns.length === 0}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Add Row
          </Button>
        ) : null}
      </div>

      {columns.length === 0 ? (
        <EmptyRowsState>{readOnly ? 'No columns yet' : 'Add a column first'}</EmptyRowsState>
      ) : rows.length === 0 ? (
        <EmptyRowsState>No rows yet</EmptyRowsState>
      ) : (
        <div className="overflow-x-auto">
          <div role="table" aria-label="Appendix rows" className="min-w-fit">
            <div role="row" className="grid items-end gap-2 pb-1" style={{ gridTemplateColumns }}>
              {columns.map((column, index) => (
                <div key={column.key} role="columnheader" className="min-w-0 text-xs font-semibold text-gray-700">
                  {column.dataType === 'CodeItem' && column.codeItemCode ? (
                    <div className="mb-0.5"><CodeItemChip code={column.codeItemCode} tone="light" /></div>
                  ) : null}
                  <span className="block truncate">{formatColumnHeader(column, `Column ${index + 1}`)}</span>
                </div>
              ))}
              {readOnly ? null : <span />}
            </div>

            <div className="space-y-1.5">
              {rows.map((row, rowIndex) => (
                <div key={row.key} role="row" className="grid items-center gap-2" style={{ gridTemplateColumns }}>
                  {columns.map((column, columnIndex) => {
                    const cellLabel = `${column.label.trim() || `Column ${columnIndex + 1}`} row ${rowIndex + 1}`
                    return (
                      <div key={column.key} role="cell" className="min-w-0">
                        <AppendixCellInput
                          column={column}
                          codeItem={findCodeItemOption(codeItems, column.codeItemCode)}
                          value={row.cells[column.key] ?? ''}
                          invalid={Boolean(cellErrors[getCellErrorKey(row.key, column.key)])}
                          disabled={readOnly}
                          ariaLabel={cellLabel}
                          onChange={(value, validateNow) => onUpdateCell(row.key, column.key, value, validateNow)}
                          onBlur={() => onValidateCell(row.key, column.key)}
                        />
                      </div>
                    )
                  })}
                  {readOnly ? null : (
                    <button
                      type="button"
                      onClick={() => onDeleteRow(row.key)}
                      disabled={disabled}
                      aria-label={`Remove row ${rowIndex + 1}`}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-[#1D5176] hover:bg-[#EEF6FF] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {firstCellError ? (
        <p role="alert" className="mt-2 flex items-center gap-1.5 text-xs text-[#B3261E]">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {firstCellError}
        </p>
      ) : null}
    </section>
  )
}

export default AppendixRowsSection
